import { Check, ChevronsUpDown, LucideIcon, Search } from "lucide-react";
import React, { useDeferredValue, useState, useEffect } from "react";

import { Button } from "@/src/components/shadcn/ui/button";
import { Command, CommandGroup, CommandInput, CommandItem, CommandList } from "@/src/components/shadcn/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/src/components/shadcn/ui/popover";
import {
  type EntityMatcherType,
  type EntityType,
  useEntityMatcherConfig,
} from "@/src/components/tasks/intake-sections/matcher-utils";
import { useExtractionAnnotationPopover } from "@/src/context/extraction-annotation-popover-context";
import { logger } from "@/src/lib/logging/logger";
import type { PayerWeighting } from "@/src/lib/payer-search";
import { cn } from "@/src/lib/shadcn-utils";

interface MatcherCommandItemProps {
  value: string;
  label: string;
  isSelected: boolean;
  onSelect: () => void;
  icon: LucideIcon;
  badge?: React.ReactNode;
  className?: string;
}

const MatcherCommandItem = ({
  value,
  icon: Icon,
  label,
  isSelected,
  onSelect,
  className,
  badge,
}: MatcherCommandItemProps) => {
  return (
    <CommandItem value={value} onSelect={onSelect} className={cn("flex items-center", className)}>
      <Check className={cn("mr-1 h-3 w-3 shrink-0", isSelected ? "opacity-100" : "opacity-0")} />
      <Icon className="mr-1.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
      <div className="flex items-center w-full">
        <span className="truncate mr-2">{label}</span>
        {badge}
      </div>
    </CommandItem>
  );
};

interface EntityMatcherProps<T extends EntityMatcherType> {
  // Entity type - determines configuration and external data sources
  type: EntityType;

  // Entities that are already persisted in the DB
  existingEntities?: T[] | undefined;
  isLoadingExistingEntities?: boolean;

  // Entity rendered as selected in the <Command> UI
  entity: T | null;
  onEntitySelected: (entity: T | null) => void;

  // Create new option
  showCreateNewOption?: boolean;
  weighting?: PayerWeighting;

  // Not listed option
  showNotListedOption?: boolean;
  isNotListedSelected?: boolean;
  onSelectNotListed?: () => void;

  // Match results from block matching (e.g., patient matching)
  // Structure: { entity: T | Partial<T>; confidence: number }[]
  matchResults?: Array<{ entity: T | Partial<T>; confidence: number }>;

  // Tab index for keyboard navigation
  tabIndex?: number;

  // Disable external search - if true, will not use external data sources (e.g., NPI, geocoding)
  disableExternalSearch?: boolean;

  // Restrict the dropdown to supplied match results plus the create-new option.
  restrictToMatchResults?: boolean;

  // Require an explicit selection even when the current entity has no persisted ID.
  requiresExplicitSelection?: boolean;
}

function EntityMatcher<T extends EntityMatcherType>({
  type,
  existingEntities,
  isLoadingExistingEntities,
  entity,
  onEntitySelected,
  showCreateNewOption = true,
  weighting,
  matchResults,
  tabIndex,
  disableExternalSearch = false,
  restrictToMatchResults = false,
  requiresExplicitSelection = false,
  showNotListedOption = false,
  isNotListedSelected = false,
  onSelectNotListed,
}: EntityMatcherProps<T>) {
  const openExtractionAnnotation = useExtractionAnnotationPopover();
  const { search, displayConfig, externalDataSources, generateInitialSearchQuery } = useEntityMatcherConfig(type);

  const filteredExternalDataSources = disableExternalSearch || restrictToMatchResults ? [] : externalDataSources;
  // Entity can be partial; generateInitialSearchQuery handles optional fields.
  const initialSearchQuery = entity && generateInitialSearchQuery ? generateInitialSearchQuery(entity) : "";

  const [open, setOpen] = useState(false);
  const [commandSelection, setCommandSelection] = useState<{ query: string; value: string } | null>(null);
  const [searchInput, setSearchInput] = useState(initialSearchQuery);
  const deferredSearch = useDeferredValue(searchInput.trim());

  const entityId = entity?.id;
  const isCreateNewSelected = showCreateNewOption && !entityId && !requiresExplicitSelection;

  const entities = existingEntities ?? [];
  // Always call search when weighting is provided, even with an empty query, to apply weighting logic.
  const searchResults =
    deferredSearch || (weighting && weighting.length > 0)
      ? search(entities, deferredSearch || "", weighting)
      : entities.slice(0, 20);

  // Exclude match results only when searchInput is empty, matching validMatchResults.
  const matchResultIds = new Set(
    !searchInput && matchResults
      ? matchResults
          .filter((match) => {
            const matchEntity = match.entity as T;

            return matchEntity && "id" in matchEntity && matchEntity.id && matchEntity.id !== entityId;
          })
          .map((match) => {
            const matchEntity = match.entity as T;

            return matchEntity && "id" in matchEntity ? matchEntity.id : null;
          })
          .filter((id): id is string => id !== null)
      : []
  );

  // Render a selected persisted entity and potential matches separately from the existing entity list.
  const filteredEntities = (restrictToMatchResults ? [] : searchResults).filter((candidate) => {
    if (entityId && candidate.id === entityId) return false;
    if (matchResultIds.has(candidate.id)) return false;

    return true;
  });

  const firstExisting = filteredEntities[0];
  const firstExternal = filteredExternalDataSources.flatMap((source) => source.results)[0];
  const firstMatchId = matchResults?.find((match) => match.entity.id)?.entity.id;
  const defaultCommandValue =
    firstMatchId ?? firstExisting?.id ?? firstExternal?.id ?? (showCreateNewOption ? "create-new" : "");

  const selectedCommandValue =
    commandSelection?.query === deferredSearch ? commandSelection.value : defaultCommandValue;

  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen && !entityId && initialSearchQuery) {
      setSearchInput(initialSearchQuery);
    }

    setOpen(nextOpen);
  };

  const handleSelectEntity = (selectedEntity: T | null) => {
    onEntitySelected(selectedEntity);

    setOpen(false);
    openExtractionAnnotation?.();

    setSearchInput("");
    setCommandSelection(null);

    filteredExternalDataSources.forEach((source) => {
      source.search("");
    });
  };

  const validMatchResults = (() => {
    if (!matchResults || matchResults.length === 0 || (!restrictToMatchResults && searchInput)) return [];

    return matchResults
      .filter((match) => {
        const matchEntity = match.entity as T;

        return matchEntity && "id" in matchEntity && matchEntity.id && matchEntity.id !== entityId;
      }) // Exclude currently selected entity and invalid matches
      .map((match) => {
        const matchEntity = match.entity as T;

        if (!matchEntity || !("id" in matchEntity) || !matchEntity.id) return null;

        return (
          <MatcherCommandItem
            key={matchEntity.id}
            value={matchEntity.id}
            icon={displayConfig.entityIcon}
            label={
              displayConfig.getPotentialMatchDisplayName?.(matchEntity) ?? displayConfig.getDisplayName(matchEntity)
            }
            isSelected={entityId === matchEntity.id}
            onSelect={() => handleSelectEntity(matchEntity)}
          />
        );
      })
      .filter((item): item is React.ReactElement => item !== null);
  })();

  // Trigger external data source searches
  useEffect(() => {
    if (disableExternalSearch || restrictToMatchResults) return;

    // TODO: Can parallelize if we ever have the need for multiple external data sources for a single matcher
    externalDataSources.forEach((source) => {
      if (source.shouldShow(deferredSearch)) {
        try {
          source.search(deferredSearch);
        } catch (error) {
          logger.error("Error searching external data source", {
            error,
          });
        }
      } else {
        source.search("");
      }
    });
  }, [deferredSearch, disableExternalSearch, externalDataSources, filteredEntities.length, restrictToMatchResults]);

  if (isLoadingExistingEntities) {
    return (
      <div className="mb-2 mt-2 w-full">
        <Button
          variant="outline"
          disabled
          className="w-full flex items-center justify-between rounded-md shadow-md bg-input text-sm p-2 border-accent"
        >
          <div className="min-w-0 flex-1 flex items-center">
            <displayConfig.entityIcon className="mr-2 h-4 w-4 shrink-0 text-muted-foreground" />
            <span className="truncate">{`Loading ${displayConfig.entityListTitle.toLowerCase()}...`}</span>
          </div>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </div>
    );
  }

  return (
    <div className="mb-2 mt-2 w-full">
      <Popover open={open} onOpenChange={handleOpenChange}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            tabIndex={tabIndex}
            className="w-full flex items-center justify-between rounded-md shadow-md bg-input text-sm hover:bg-accent p-2 border-accent"
          >
            <div className="min-w-0 flex-1 flex items-center">
              {isNotListedSelected ? (
                <>
                  <displayConfig.entityIcon className="mr-2 h-4 w-4 shrink-0 text-muted-foreground" />
                  <span className="truncate">Not Listed</span>
                </>
              ) : !entity ? (
                <>
                  {showCreateNewOption ? (
                    <>
                      <displayConfig.createNewIcon className="mr-2 h-4 w-4 shrink-0 text-muted-foreground" />
                      <span className="truncate">{displayConfig.createNewTitle}</span>
                    </>
                  ) : (
                    <>
                      <Search className="mr-2 h-4 w-4 shrink-0 text-muted-foreground" />
                      <span className="truncate">{displayConfig.placeholder}</span>
                    </>
                  )}
                </>
              ) : entity ? (
                <>
                  <displayConfig.entityIcon className="mr-2 h-4 w-4 shrink-0 text-muted-foreground" />
                  <span className="truncate">{displayConfig.getDisplayName(entity)}</span>
                  <div className="ml-auto">{displayConfig?.renderBadge?.(entity)}</div>
                </>
              ) : null}
            </div>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[var(--radix-popover-trigger-width)] min-w-[400px] p-0 border-muted/50 rounded-sm shadow-sm bg-popover">
          <Command
            className="w-full"
            shouldFilter={false}
            value={selectedCommandValue}
            onValueChange={(value) => {
              setCommandSelection({ query: deferredSearch, value });
            }}
          >
            {!restrictToMatchResults && (
              <CommandInput
                placeholder={displayConfig.placeholder}
                value={searchInput}
                onValueChange={(value) => {
                  setSearchInput(value);

                  if (!open) setOpen(true);
                }}
              />
            )}
            <CommandList>
              {/* Matched entity */}
              {entity?.id && !searchInput && (
                <CommandGroup heading={"Currently selected"}>
                  <MatcherCommandItem
                    key={entity.id}
                    value={entity.id}
                    icon={displayConfig.entityIcon}
                    label={displayConfig.getDisplayName(entity)}
                    isSelected={true}
                    onSelect={() => handleSelectEntity(entity)}
                    badge={displayConfig.renderBadge && displayConfig.renderBadge(entity)}
                  />
                </CommandGroup>
              )}

              {/* Match results from block matching */}
              {validMatchResults.length > 0 && (
                <CommandGroup heading={"Potential matches"}>{validMatchResults}</CommandGroup>
              )}

              {/* Existing/internal entities */}
              {existingEntities?.length ? (
                <CommandGroup heading={displayConfig.entityListTitle}>
                  {filteredEntities.length ? (
                    <>
                      {filteredEntities.map((existingEntity) => (
                        <MatcherCommandItem
                          key={existingEntity.id}
                          value={existingEntity.id}
                          icon={displayConfig.entityIcon}
                          label={displayConfig.getDisplayName(existingEntity)}
                          isSelected={entity?.id === existingEntity.id}
                          onSelect={() => handleSelectEntity(existingEntity as T)}
                          badge={displayConfig.renderBadge && displayConfig.renderBadge(existingEntity)}
                        />
                      ))}
                    </>
                  ) : (
                    <div className="px-6 py-1.5 text-sm">
                      No matching existing {displayConfig.entityListTitle.toLowerCase()} found.
                    </div>
                  )}
                </CommandGroup>
              ) : null}

              {/* External data source results */}
              {filteredExternalDataSources.map((source, index) => {
                const sourceResults = source.results;
                const shouldShow = source.shouldShow(deferredSearch);
                const title =
                  !source.isLoading && !source.error && sourceResults?.length
                    ? source.groupTitle + " (" + sourceResults.length + " results)"
                    : source.groupTitle;

                if (!shouldShow) return null;

                return (
                  <CommandGroup key={`external-${index}`} heading={title}>
                    {source.isLoading ? (
                      <div className="px-6 py-1.5 text-sm">Searching...</div>
                    ) : source.error ? (
                      <div className="px-6 py-1.5 text-sm">{source.error}</div>
                    ) : sourceResults?.length ? (
                      sourceResults.map((externalEntity) => (
                        <MatcherCommandItem
                          key={`external-${index}-${externalEntity.id}`}
                          value={externalEntity.id}
                          icon={source.getIcon(externalEntity)}
                          label={source.getDisplayName(externalEntity)}
                          isSelected={entity?.id === externalEntity.id}
                          onSelect={() => handleSelectEntity(externalEntity as T)}
                          badge={displayConfig?.renderBadge?.(externalEntity)}
                        />
                      ))
                    ) : (
                      <div className="px-6 py-1.5 text-sm">No matching {source.groupTitle} found.</div>
                    )}
                  </CommandGroup>
                );
              })}

              {/* Not listed option */}
              {showNotListedOption && (
                <CommandGroup>
                  <MatcherCommandItem
                    key="not-listed"
                    value="not-listed"
                    icon={displayConfig.entityIcon}
                    label="Not Listed"
                    isSelected={isNotListedSelected}
                    onSelect={() => {
                      setOpen(false);
                      onSelectNotListed?.();
                      openExtractionAnnotation?.();
                    }}
                  />
                </CommandGroup>
              )}

              {/* Create new option */}
              {showCreateNewOption && (
                <CommandGroup heading={"Create"}>
                  <MatcherCommandItem
                    key="create-new"
                    value="create-new"
                    icon={displayConfig.createNewIcon}
                    label={displayConfig.createNewTitle}
                    isSelected={isCreateNewSelected}
                    onSelect={() => {
                      if (isCreateNewSelected && !requiresExplicitSelection) {
                        setOpen(false);

                        return;
                      }

                      handleSelectEntity(null);
                    }}
                  />
                </CommandGroup>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}

export { EntityMatcher };
